import type { CatalogProduct } from '../ProductPicker';

/** Suggestions at or above this score are preselected; the user can still change them. */
export const CONFIDENT_SCORE = 0.75;
const MIN_SCORE = 0.35;

export interface NameEntry {
  name: string;
  /** Every workspace row with this name, most reviewed first. */
  products: CatalogProduct[];
  tokens: string[];
  grams: Map<string, number>;
  gramCount: number;
}

export interface Suggestion {
  entry: NameEntry;
  score: number;
}

/** Lowercase, strip accents and punctuation, collapse whitespace. */
export const normaliseName = (name: string) =>
  name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const bigrams = (text: string) => {
  const padded = ` ${text} `;
  const grams = new Map<string, number>();
  for (let i = 0; i < padded.length - 1; i += 1) {
    const gram = padded.slice(i, i + 2);
    grams.set(gram, (grams.get(gram) ?? 0) + 1);
  }
  return { grams, gramCount: padded.length - 1 };
};

const toEntry = (name: string, products: CatalogProduct[]): NameEntry => {
  const norm = normaliseName(name);
  return {
    name,
    products: [...products].sort((a, b) => b.reviews - a.reviews),
    tokens: norm.split(' ').filter(Boolean),
    ...bigrams(norm),
  };
};

/** One entry per distinct product name, with precomputed bigrams. */
export const buildNameIndex = (catalog: CatalogProduct[]): NameEntry[] => {
  const byName = new Map<string, CatalogProduct[]>();
  catalog.forEach((p) => byName.set(p.name, [...(byName.get(p.name) ?? []), p]));
  return Array.from(byName, ([name, products]) => toEntry(name, products));
};

/**
 * Blend of character-bigram similarity (catches typos and spacing) and the
 * share of pasted words found at the start of a catalog word (catches
 * abbreviations and extra or missing words).
 */
const similarity = (query: NameEntry, candidate: NameEntry) => {
  let shared = 0;
  query.grams.forEach((count, gram) => {
    shared += Math.min(count, candidate.grams.get(gram) ?? 0);
  });
  const dice = (2 * shared) / (query.gramCount + candidate.gramCount);
  const found = query.tokens.filter((t) => candidate.tokens.some((c) => c.startsWith(t) || t.startsWith(c)));
  const wordRecall = query.tokens.length ? found.length / query.tokens.length : 0;
  return 0.6 * dice + 0.4 * wordRecall;
};

/** The closest catalog names to a pasted name, best first. */
/** How many suggestions each name gets. */
export const SUGGESTION_LIMIT = 10;

export const suggestNames = (pasted: string, index: NameEntry[], limit = SUGGESTION_LIMIT): Suggestion[] => {
  const query = toEntry(pasted, []);
  return index
    .map((entry) => ({ entry, score: similarity(query, entry) }))
    .filter((s) => s.score >= MIN_SCORE)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
};
